import { useState, ReactNode, useRef } from 'react';
import Tippy from '@tippyjs/react';
import ReactPlayer from 'react-player';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light.css';
import { Modal, Button, Loading } from '@plick/ui';
import { plickTheme } from '@theme/index';
import { IconArrowsMaximize, IconExclamationCircleFilled, IconRefresh } from '@tabler/icons-react';

interface VideoTooltipProps {
  videoUrl: string;
  children: ReactNode;
  title?: string;
  description?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  trigger?: 'hover' | 'click';
  onShow?: () => void;
  onHide?: () => void;
}

/**
 * Componente que muestra un tooltip con video explicativo usando Tippy.js y ReactPlayer
 * Optimizado para mostrar tutoriales y guías de uso.
 * @param {VideoTooltipProps} props - Props del componente VideoTooltip
 * @returns {JSX.Element} Componente VideoTooltip envolviendo los children con Tippy
 */
const VideoTooltip = ({
  videoUrl,
  children,
  title = 'Tutorial',
  description,
  placement = 'top',
  trigger = 'hover',
  onShow,
  onHide,
}: VideoTooltipProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleShow = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setTooltipVisible(true);
    setIsLoaded(false);
    setHasError(false);
    onShow?.();
    setTimeout(() => setIsLoaded(true), 300);
  };

  const handleHide = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setTooltipVisible(false);
      setIsLoaded(false);
      setHasError(false);
      onHide?.();
      hideTimeoutRef.current = null;
    }, 200);
  };

  const handleTooltipEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setTooltipVisible(true);
  };

  const handleTooltipLeave = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setTooltipVisible(false);
    setIsLoaded(false);
    setHasError(false);
    onHide?.();
  };

  const handleOpenModal = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setTooltipVisible(false);
    setIsModalOpen(true);
  };

  const handleToggle = () => {
    if (tooltipVisible) {
      handleTooltipLeave();
    } else {
      handleShow();
    }
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
  };

  return (
    <>
      <Tippy
        visible={tooltipVisible}
        content={
          <div onMouseEnter={handleTooltipEnter} onMouseLeave={handleTooltipLeave}>
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden" style={{ maxWidth: '400px' }}>
              {/* Video Section */}
              <div className="relative">
                {!isLoaded && !hasError ? (
                  <div className="w-full h-48 flex flex-col items-center justify-center bg-gray-50">
                    <Loading.Component />
                  </div>
                ) : null}

                {hasError ? (
                  <div className="w-full h-48 flex flex-col items-center justify-center gap-3 bg-gray-50">
                    <IconExclamationCircleFilled color={plickTheme.colors.dangerDark} />
                    <span className="text-sm text-center text-gray-600">Error al cargar el video</span>
                    <Button.Tertiary
                      onClick={() => {
                        setHasError(false);
                        setIsLoaded(false);
                        setTimeout(() => setIsLoaded(true), 100);
                      }}
                      text="Reintentar"
                      rightIcon={<IconRefresh />}
                    />
                  </div>
                ) : null}

                {isLoaded && !hasError && (
                  <div className="relative">
                    <ReactPlayer
                      url={videoUrl}
                      width="340px"
                      height="225px"
                      playing={true}
                      muted={true}
                      controls={true}
                      onError={handleError}
                      config={{
                        youtube: {
                          playerVars: {
                            showinfo: 1,
                            rel: 0,
                            modestbranding: 1,
                          },
                        },
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Content Section */}
              <div className="p-5">
                {title ? (
                  <div className="mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
                    {description ? <p className="text-sm text-gray-600 leading-relaxed">{description}</p> : null}
                  </div>
                ) : null}

                {/* Action Button */}
                <div className="flex justify-end pt-2">
                  <Button.Primary onClick={handleOpenModal} text="VER COMPLETO" rightIcon={<IconArrowsMaximize />} />
                </div>
              </div>
            </div>
          </div>
        }
        interactive={true}
        allowHTML={true}
        placement={placement}
        arrow={true}
        theme="light"
        appendTo={document.body}
        onShow={() => onShow?.()}
        onHide={() => onHide?.()}
      >
        <span
          className={`inline-block ${trigger === 'click' ? 'cursor-pointer' : 'cursor-help'}`}
          {...(trigger === 'click'
            ? { onClick: handleToggle }
            : { onMouseEnter: handleShow, onMouseLeave: handleHide })}
        >
          {children}
        </span>
      </Tippy>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div className="space-y-4 max-w-4xl">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
          </div>

          {description ? <p className="text-gray-600 text-sm">{description}</p> : null}

          <div className="flex justify-center">
            <ReactPlayer
              url={videoUrl}
              width="100%"
              height="400px"
              playing={true}
              controls={true}
              config={{
                youtube: {
                  playerVars: {
                    showinfo: 1,
                    rel: 0,
                    modestbranding: 1,
                  },
                },
              }}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default VideoTooltip;
